import { supabase } from './supabase';

// Get or create persistent client ID
const getClientId = () => {
  let clientId = localStorage.getItem('analytics_client_id');
  if (!clientId) {
    clientId = `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('analytics_client_id', clientId);
  }
  return clientId;
};

// Get session ID (changes each session)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Check if user has opted out
const isOptedOut = () => {
  return localStorage.getItem('analytics_opt_out') === 'true';
};

// Get user's location data from server-side endpoint
const getUserLocation = async () => {
  try {
    const response = await fetch('/api/geolocation');
    if (!response.ok) {
      return {
        country_code: null,
        country: null,
        region: null,
        region_name: null,
        city: null,
        zip: null,
        latitude: null,
        longitude: null,
        timezone: null,
        isp: null,
        org: null,
        ip_address: null
      };
    }
    const data = await response.json();
    return {
      country_code: data.country_code || null,
      country: data.country || null,
      region: data.region || null,
      region_name: data.region_name || null,
      city: data.city || null,
      zip: data.zip || null,
      latitude: data.latitude || null,
      longitude: data.longitude || null,
      timezone: data.timezone || null,
      isp: data.isp || null,
      org: data.org || null,
      ip_address: data.ip_address || null
    };
  } catch (error) {
    console.error('Error getting location:', error);
    return {
      country_code: null,
      country: null,
      region: null,
      region_name: null,
      city: null,
      zip: null,
      latitude: null,
      longitude: null,
      timezone: null,
      isp: null,
      org: null,
      ip_address: null
    };
  }
};

// Track session start
export const trackSessionStart = async (userId = null) => {
  if (isOptedOut()) return;
  
  try {
    const sessionId = getSessionId();
    const clientId = getClientId();
    const location = await getUserLocation();
    
    // Check if session already exists
    const { data: existing } = await supabase
      .from('analytics_sessions')
      .select('id')
      .eq('session_id', sessionId)
      .single();
    
    if (!existing) {
      // New session - create it
      await supabase.from('analytics_sessions').insert({
        session_id: sessionId,
        client_id: clientId,
        user_id: userId || null,
        started_at: new Date().toISOString(),
        country_code: location.country_code,
        country: location.country,
        region: location.region,
        region_name: location.region_name,
        city: location.city,
        ip_address: location.ip_address,
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
      });
    }
  } catch (error) {
    console.error('Error tracking session start:', error);
  }
};

// Track session end
export const trackSessionEnd = async () => {
  if (isOptedOut()) return;
  
  try {
    const sessionId = getSessionId();
    if (!sessionId) return;
    
    const endedAt = new Date().toISOString();
    
    // Get session start time to calculate duration
    const { data: session } = await supabase
      .from('analytics_sessions')
      .select('started_at')
      .eq('session_id', sessionId)
      .single();
    
    if (session && session.started_at) {
      const duration = Math.floor(
        (new Date(endedAt) - new Date(session.started_at)) / 1000
      );
      
      await supabase
        .from('analytics_sessions')
        .update({
          ended_at: endedAt,
          duration_seconds: duration
        })
        .eq('session_id', sessionId);
    } else {
      // Session not found, just update with end time
      await supabase
        .from('analytics_sessions')
        .update({
          ended_at: endedAt
        })
        .eq('session_id', sessionId);
    }
  } catch (error) {
    console.error('Error tracking session end:', error);
  }
};

// Track page view
export const trackPageView = async (pagePath, userId = null) => {
  // Don't track if opted out
  if (isOptedOut()) {
    return;
  }

  try {
    const clientId = getClientId();
    const sessionId = getSessionId();
    const location = await getUserLocation();
    
    // Ensure session exists (track session start if needed)
    await trackSessionStart(userId);
    
    // Insert page view
    await supabase.from('analytics_page_views').insert({
      client_id: clientId,
      user_id: userId || null,
      page_path: pagePath,
      session_id: sessionId,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      country_code: location.country_code,
      country: location.country,
      region: location.region,
      region_name: location.region_name,
      city: location.city,
      zip: location.zip,
      latitude: location.latitude,
      longitude: location.longitude,
      timezone: location.timezone,
      isp: location.isp,
      org: location.org,
      ip_address: location.ip_address,
      created_at: new Date().toISOString(),
    });
    
    // Increment session page count
    await supabase.rpc('increment_session_page_count', {
      session_id_param: sessionId
    });
  } catch (error) {
    console.error('Analytics tracking error:', error);
  }
};

// Track custom events
export const trackEvent = async (eventName, eventData = {}, userId = null) => {
  if (isOptedOut()) {
    return;
  }

  try {
    const clientId = getClientId();
    const sessionId = getSessionId();
    const location = await getUserLocation();
    
    await supabase.from('analytics_events').insert({
      client_id: clientId,
      user_id: userId || null,
      event_name: eventName,
      event_data: eventData,
      session_id: sessionId,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      country_code: location.country_code,
      country: location.country,
      region: location.region,
      region_name: location.region_name,
      city: location.city,
      zip: location.zip,
      latitude: location.latitude,
      longitude: location.longitude,
      timezone: location.timezone,
      isp: location.isp,
      org: location.org,
      ip_address: location.ip_address,
      created_at: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Analytics event tracking error:', error);
  }
};

// Helper function to track button clicks
export const trackButtonClick = async (buttonInfo, userId = null) => {
  const eventData = {
    button_id: buttonInfo.id || null,
    button_text: buttonInfo.text || null,
    button_type: buttonInfo.type || 'button',
    page_path: window.location.pathname + window.location.search,
    page_name: buttonInfo.page || null,
    ...buttonInfo.additionalData
  };
  
  await trackEvent('button_click', eventData, userId);
};
